/**
 * usePixiObjects
 *
 * Manages PixiJS scene graph objects from Redux state.
 * Creates, updates and destroys PixiJS display objects as Redux objects change.
 */
import { Graphics, Sprite, Texture, Container, FillGradient, AlphaMask, TextureMatrix } from 'pixi.js'
import polygonClipping from 'polygon-clipping'
import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { STAGE_LEFT, STAGE_TOP } from '~/lib/constants'
import { localPivot, localScale } from '~/lib/objectBounds'
import { getObjectInitial } from '~/lib/params'
import { resolveAssetUrl, resolveAssetUrlSync } from '~/lib/resolveAssetUrl'
import { getShader, isShaderSource, onShadersChanged } from '~/lib/shaders'
import { drawsSourceFill, FILL_NODE_POSE, fillRef, isSourceFill } from '~/lib/sourceFill'
import { editsOver } from '~/lib/sourceWrites'
import { hasFrame, onFirstFrame, releaseSource, retainSource, wantSource } from '~/lib/sources/textures'
import {
	bindSampleTexture,
	carryShaderClock,
	createSampleMesh,
	createShaderMesh,
	forgetShaderBuilds,
	updateSampleMesh,
	updateShaderMesh,
	writeSampleValues,
	writeShaderValues,
} from './pixiShaderMesh'
import { followShape } from './livePose'
import PixiShape, { stringToRawPath, gradientEndpoints } from './PixiShape'
import { useGetAssetsQuery } from '~/store/api'
import { selectObjects } from '~/store/live/selectors'
import { selectCameraDevice } from '~/store/camera/selectors'
import { resolveFillSource } from '~/lib/sources/fillSources'

const selectSourceSettings = s => s.live.source_settings

import { parseHexAlpha } from '~/lib/pixiColor'

// Monkey-patch AlphaMask.test to accept flagged containers (not just Sprites)
const _origAlphaMaskTest = AlphaMask.test
AlphaMask.test = mask => mask?._useAlphaMask === true || _origAlphaMaskTest(mask)

// Patch TextureMatrix stale-texture bug (pixijs#11995)
const origTexMatDesc = Object.getOwnPropertyDescriptor(TextureMatrix.prototype, 'texture')
Object.defineProperty(TextureMatrix.prototype, 'texture', {
	set(value) {
		if (this._texture !== value) {
			this._texture?.removeListener('update', this.update, this)
			this._texture = value
			this._texture.addListener('update', this.update, this)
		}
		this.update()
	},
	get: origTexMatDesc.get,
})

// Redraw a background Graphics with a Redux fill value
export const drawBgFill = (bg, fill, w, h) => {
	bg.clear()
	const pf = toPixiFill(fill)
	if (pf.fill) {
		const srcGrad = pf.fill
		const opts = {
			type: srcGrad.type,
			colorStops: srcGrad.colorStops,
			textureSpace: 'global',
		}
		if (srcGrad.type === 'radial') {
			opts.center = { x: (srcGrad.center?.x ?? 0.5) * w, y: (srcGrad.center?.y ?? 0.5) * h }
			opts.outerCenter = { x: (srcGrad.outerCenter?.x ?? 0.5) * w, y: (srcGrad.outerCenter?.y ?? 0.5) * h }
			opts.innerRadius = (srcGrad.innerRadius ?? 0) * Math.max(w, h)
			opts.outerRadius = (srcGrad.outerRadius ?? 0.5) * Math.max(w, h)
		} else {
			const ep = gradientEndpoints(pf._angle || 0, 0, 0, w, h, srcGrad.colorStops)
			opts.start = ep.start
			opts.end = ep.end
			opts.colorStops = ep.colorStops
		}
		bg.rect(0, 0, w, h).fill({ fill: new FillGradient(opts) })
	} else {
		bg.rect(0, 0, w, h).fill({ color: pf.color, alpha: pf.alpha })
	}
}

const logger = console.scope('usePixiObjects')

/**
 * Contract `blendMode` → the Pixi blend name that renders it.
 *
 * ⚠️ MUST COVER THE CONTRACT ENUM. The fallback below is `|| 'normal'`, so a value declared in the contract but missing here renders as no blend at all — nothing throws, the user picks a mode and sees no change. `objectDefaults.test.js` gates the coverage; the Pixi names stay here because they are a renderer fact the contract has no business knowing.
 */
export const BLEND_MAP = {
	'source-over': 'normal',
	// No Pixi equivalent, and nothing else in the app implements it — selecting it is a no-op.
	'destination-in': 'normal',
	'destination-out': 'erase',
	lighter: 'add',
}

// Returns a PixiJS FillStyle object: { color, alpha } for solid, { fill: FillGradient } for gradient
export const toPixiFill = fill => {
	if (fill === '') return { color: 0x000000, alpha: 0 }
	if (!fill) return { color: 0x000000, alpha: 1 }
	if (typeof fill === 'number') return { color: fill, alpha: 1 }
	if (typeof fill === 'string') return parseHexAlpha(fill)
	// The fill node paints it; the shape draws only its stroke.
	if (isSourceFill(fill)) return { color: 0x000000, alpha: 0 }
	if (!fill.stops?.length) return { color: 0x000000, alpha: 1 }

	// Gradient — per-stop alpha is encoded in each stop's color (8-digit hex),
	// PixiJS ColorSource handles this natively
	const stops = fill.stops.map(s => ({ offset: s.offset, color: s.color }))
	if (fill.type === 'linear') {
		return {
			fill: new FillGradient({
				type: 'linear',
				start: { x: 0, y: 0 },
				end: { x: 1, y: 0 },
				colorStops: stops,
				textureSpace: 'local',
			}),
			_angle: fill.angle || 0,
		}
	}
	if (fill.type === 'radial') {
		return {
			fill: new FillGradient({
				type: 'radial',
				center: { x: 0.5, y: 0.5 },
				innerRadius: 0,
				outerCenter: { x: 0.5, y: 0.5 },
				outerRadius: 0.5,
				colorStops: stops,
				textureSpace: 'local',
			}),
		}
	}
	return { color: 0x000000, alpha: 1 }
}

// ⚠️ Keyed by SOURCE, never the resolved URL, which changes across an asset re-fetch; shared by every sprite drawing it, so only `evictTextures` may destroy one.
const residentTextures = new Map()
const inflightTextures = new Map()

export const getCachedTexture = source => residentTextures.get(source) ?? null

export const loadImageTextureFor = source => {
	const resident = residentTextures.get(source)
	if (resident) return Promise.resolve(resident)
	const pending = inflightTextures.get(source)
	if (pending) return pending

	const displayUrl = resolveAssetUrlSync(source)
	if (!displayUrl) return null

	const load = new Promise((resolve, reject) => {
		const img = new Image()
		if (!displayUrl.startsWith('blob:')) img.crossOrigin = 'use-credentials'
		// `decode()` keeps a large decode off the main thread, and a failed decode still draws — only `onerror` is fatal.
		img.onload = () =>
			img
				.decode()
				.catch(() => {})
				.then(() => resolve(Texture.from(img)))
		img.onerror = reject
		img.src = displayUrl
	})
		.then(tex => {
			residentTextures.set(source, tex)
			return tex
		})
		.finally(() => inflightTextures.delete(source))

	// ⚠️ Marks the chain handled without replacing it: callers share this exact promise and must still see the rejection, but the cache's own reference has no consumer and would reject into nothing.
	load.catch(() => {})
	inflightTextures.set(source, load)
	return load
}

// Returns the failed sources.
export const primeTextures = async (sources, onProgress) => {
	const total = sources.length
	const failed = []
	let done = 0
	onProgress?.(0, total)

	await Promise.all(
		sources.map(async src => {
			try {
				const tex = await (loadImageTextureFor(src) ?? Promise.reject(new Error('unresolved')))
				await window.pixi_app?.renderer?.prepare?.upload(tex)
			} catch {
				failed.push(src)
			}
			onProgress?.(++done, total)
		})
	)

	return failed
}

// ⚠️ `keep` is the settled source list across every remaining preset — presets share images, so evicting per-preset destroys what the survivors still draw.
export const evictTextures = keep => {
	for (const [source, tex] of residentTextures) {
		if (keep.has(source)) continue
		residentTextures.delete(source)
		tex.destroy(true)
	}
}

/** Put Pixi's transform origin on the stored pivot: a Sprite's `anchor`, a Graphics' `pivot` in `data` units, a group Container's 0 (its local origin is its pivot). */
export const applyPivot = (pixiObj, obj) => {
	switch (obj.type) {
		case 'image':
			pixiObj.anchor.set(obj.pivotX ?? 0.5, obj.pivotY ?? 0.5)
			break
		case 'rect':
		case 'circle':
		case 'path':
		case 'brush': {
			const s = localScale(obj)
			pixiObj.setDrawPose(localPivot(obj), s.x, s.y)
			break
		}
		case 'group':
			pixiObj.pivot.set(0, 0)
			break
	}
}

// ⚠️ A switch HOLDS the bound picture until the wanted source has a frame, so an acquiring camera never blanks the shape.
const bindSource = (consumer, node, sourceId) => {
	wantSource(consumer, sourceId)
	const bound = node._sourceId ?? null
	if (bound === sourceId || (sourceId && bound && !hasFrame(sourceId))) return
	bindSampleTexture(node, sourceId ? retainSource(sourceId) : Texture.EMPTY)
	if (bound) releaseSource(bound)
	node._sourceId = sourceId
}

/** A shape's fill backing: `'pipeline'` for any subset (a switch between two re-variants the node in place), a standalone shader's source, `'sample'` for a camera, screen or file, null for a colour or gradient. */
export const fillBacking = obj => {
	if (!drawsSourceFill(obj)) return null
	const { source } = obj.fill
	if (!isShaderSource(source)) return 'sample'
	return getShader(source)?.locks ? 'pipeline' : source
}

const fillConsumer = id => `fill:${id}`

/** What a fill node draws: a node mapped onto its own shape, its picture settings from the preset's bag for the source. */
export const fillNodeObj = (obj, bags) => {
	const { source } = obj.fill
	const bag = bags?.[source] ?? {}
	return {
		...FILL_NODE_POSE,
		id: obj.id,
		source,
		surfaces: [fillRef(obj)],
		source_settings: { [source]: bag },
		paused: bag.paused === true,
		zoom: bag.zoom ?? 100,
		panX: bag.panX ?? 0,
		panY: bag.panY ?? 0,
		scaleX: bag.mirror ? -1 : 1,
	}
}

/** Tear down one fill node; a fade drops the ones it dissolved out through here. */
export const dropFillNode = node => {
	if (node._sourceId) releaseSource(node._sourceId)
	node._sourceId = null
	clearMask(node)
	node.parent?.removeChild(node)
	if (!node.destroyed) node.destroy()
}

export const destroyFill = (id, entry) => {
	const node = entry.fill
	entry.fill = null
	entry.fillBacking = null
	if (!node) return
	wantSource(fillConsumer(id), null)
	dropFillNode(node)
}

/** Build, rebuild, bind and update a shape's fill node; it follows its shape (`followShape`). */
const syncFill = (id, entry, obj, { env, selectedCamera, sourceSettings }) => {
	const backing = fillBacking(obj)
	if (entry.fill && (entry.fillBacking !== backing || entry.fill.destroyed)) destroyFill(id, entry)
	if (!backing) return
	const nodeObj = fillNodeObj(obj, sourceSettings)
	let node = entry.fill
	if (!node) {
		node =
			backing === 'sample'
				? createSampleMesh(nodeObj, env, { fill: true })
				: createShaderMesh(nodeObj, env, { fill: true })
		if (!node) return
		node._sourceId = null
		entry.fill = node
		entry.fillBacking = backing
	}
	node._fillSource = nodeObj.source
	if (node._sample)
		bindSource(
			fillConsumer(id),
			node,
			resolveFillSource(nodeObj.source, { device: selectedCamera, bags: sourceSettings })
		)
	const init = key => nodeObj[key]
	if (node._sample) updateSampleMesh(node, nodeObj, init, env)
	else updateShaderMesh(node, nodeObj, init, env)
	// A stencil is never drawn, and a sample shows nothing until bound.
	node._shown = !obj.maskFor && (!node._sample || !!node._sourceId)
	followShape(node, entry.pixiObj)
}

/** A fade's fill swap: `entry` takes a fresh node for `obj` and the outgoing one is returned, still on stage, for the fade to dissolve and drop. */
const swapFill = (id, entry, obj, ctx) => {
	const old = entry.fill
	entry.fill = null
	entry.fillBacking = null
	if (old) wantSource(fillConsumer(id), null)
	syncFill(id, entry, obj, ctx)
	return old
}

const createPixiObject = obj => {
	const { type } = obj

	switch (type) {
		case 'rect':
		case 'circle':
		case 'path':
		case 'brush':
			return new PixiShape()
		case 'image': {
			const sprite = new Sprite(Texture.EMPTY)
			return sprite
		}
		case 'group': {
			return new Container()
		}
		default: {
			logger.warn('Unknown object type:', type)
			return null
		}
	}
}

export const updatePixiObject = (pixiObj, obj) => {
	const { type, fill: rawFill, stroke, strokeWidth } = obj

	// ⚠️ Absence resolves through the CONTRACT, never a literal default.
	const init = key => obj[key] ?? getObjectInitial(type, key)
	const x = init('x')
	const y = init('y')
	const width = init('width')
	const height = init('height')
	const rotation = init('rotation')
	const opacity = init('opacity')
	// `fill` carried `omit: false` on the four shape types, so it is one of the seven keys whose
	// deletion makes it absent at its initial. `toPixiFill(undefined)` already yields opaque black,
	// which happens to equal the contract initial — resolving through the contract makes that
	// agreement a fact rather than a coincidence between two independent defaults.
	//
	// image/group declare no `fill` at all, so this stays `undefined` for them.
	const fill = toPixiFill(rawFill ?? getObjectInitial(type, 'fill'))
	// ⚠️ Same contract rule as above, and the same latent bug: `minWidth || 0` restated a default of 0
	// against a contract initial of 20, and `||` additionally swallowed a deliberate 0. `maxWidth` was
	// a second fallback naming a param the contract has never declared — dead, and unreachable only
	// because `omit: false` keeps the key present.
	const brushMaxWidth = obj.type === 'brush' ? init('minWidth') + init('widthGrowth') : undefined

	// Common transform. `x`/`y` name the pivot, so Pixi turns and scales every object about it (context/geometry.md §The stored transform fields).
	pixiObj.x = x
	pixiObj.y = y
	pixiObj.rotation = (rotation * Math.PI) / 180
	applyPivot(pixiObj, obj)
	// Skip fade sync while a transition runs: Redux stays at the FROM preset for the whole morph,
	// so writing these here would fight the opacity tween and re-pop a `visible` crossing back to
	// its FROM value. transitionManager settles both when the morph completes (and on kill()).
	//
	// NOTE the flag is GLOBAL but this runs PER OBJECT, so this freezes alpha/visible sync for
	// EVERY object, not just the ones the timeline actually tweens. Known consequence: `opacity`
	// is a supported live prop, and live props are excluded from the tween precisely so they stay
	// hand-drivable mid-transition — but an opacity edit on an untouched object is dropped here
	// and only lands when setLiveObject/setLiveProperties run on complete, so the slider looks
	// dead for the rest of the fade and then pops. Bounded: nothing is corrupted and onComplete
	// reconciles, but the write is invisible for up to fade_duration seconds.
	//
	// TODO: make this per-object — skip only when the timeline actually owns THIS object's fade.
	// transitionManager already tracks exactly that (see §Object ownership in
	// context/transitions.md), so it could expose an `ownsFade(id)` query for this guard to use.
	if (!window.pixi_transition_active) {
		pixiObj.alpha = opacity / 100
		pixiObj.visible = obj.visible !== false && !obj.inpaint_mask
	}
	pixiObj.blendMode = BLEND_MAP[obj.blendMode] || 'normal'

	// Only `group` takes its scale here: an image composes its own below, and a vector's goes into its points (`applyPivot`).
	if (type === 'group') {
		pixiObj.scale.x = init('scaleX')
		pixiObj.scale.y = init('scaleY')
	}

	switch (type) {
		case 'brush': {
			pixiObj.updateBrushStyle(fill, brushMaxWidth || strokeWidth, obj.strokeCap)
			if (!obj.data) break
			if (!window.pixi_transition_active) {
				if (pixiObj._sourceData !== obj.data) {
					pixiObj.setRawPathSilent(stringToRawPath(obj.data))
					pixiObj._sourceData = obj.data
				}
				pixiObj._draw()
			}
			break
		}
		case 'rect':
		case 'circle':
		case 'path': {
			pixiObj.updateStyle(fill, stroke, strokeWidth)
			if (!obj.data) break
			// Skip redraw if GSAP transition is currently animating this object
			if (!window.pixi_transition_active) {
				if (pixiObj._sourceData !== obj.data) {
					pixiObj.setRawPathSilent(stringToRawPath(obj.data))
					pixiObj._sourceData = obj.data
				}
				pixiObj._draw()
			}
			break
		}
		case 'image': {
			// Texture loading handled by useEffect in usePixiObjects (requires auth)
			if (pixiObj.texture !== Texture.EMPTY) {
				// Set scale explicitly — Sprite's .width setter preserves the sign of scale.x, so
				// assigning width would silently keep a stale mirror.
				const texW = pixiObj.texture.width || 1
				const texH = pixiObj.texture.height || 1
				const sx = init('scaleX')
				const sy = init('scaleY')
				// The anchor is on the pivot, so a negative scale mirrors in place: no offset.
				pixiObj.scale.x = (sx * width) / texW
				pixiObj.scale.y = (sy * height) / texH
			}
			// Apply eraser mask as inverted Graphics mask
			if (obj.eraserMask) {
				let masks
				try {
					masks = JSON.parse(obj.eraserMask)
				} catch {
					masks = null
				}
				if (masks && masks.length) {
					const g = pixiObj._eraserMaskGfx || new Graphics()
					g.clear()
					// Compute visible region: image rect minus all eraser polygons.
					// Use polygon-clipping.difference so we don't rely on cut() for masking.
					const imageRect = [
						[0, 0],
						[width, 0],
						[width, height],
						[0, height],
						[0, 0],
					]
					const eraserPolys = masks.filter(r => r && r.length >= 3).map(r => [r])
					let visible = [[imageRect]]
					try {
						for (const ep of eraserPolys) {
							visible = polygonClipping.difference(visible, ep)
							if (!visible || !visible.length) break
						}
					} catch {
						/* ignore */
					}
					// Draw visible region polygons (may have holes)
					if (visible && visible.length) {
						for (const poly of visible) {
							for (let ri = 0; ri < poly.length; ri++) {
								const ring = poly[ri]
								if (!ring || ring.length < 3) continue
								g.moveTo(ring[0][0], ring[0][1])
								for (let i = 1; i < ring.length; i++) {
									g.lineTo(ring[i][0], ring[i][1])
								}
								g.closePath()
							}
						}
						g.fill(0xffffff)
					}
					if (!pixiObj._eraserMaskGfx) {
						pixiObj._eraserMaskGfx = g
						pixiObj.parent?.addChild(g)
					}
					pixiObj.mask = g
					// Mask coords are in object-local size units [0..width, 0..height], not texture-native, so the mask takes
					// the image's pose with the pivot in those units rather than the sprite's texture-scaled one.
					g.x = x
					g.y = y
					g.pivot.set((obj.pivotX ?? 0.5) * width, (obj.pivotY ?? 0.5) * height)
					g.rotation = (rotation * Math.PI) / 180
					g.scale.x = init('scaleX')
					g.scale.y = init('scaleY')
				}
			} else if (pixiObj._eraserMaskGfx) {
				pixiObj.mask = null
				pixiObj._eraserMaskGfx.parent?.removeChild(pixiObj._eraserMaskGfx)
				if (!pixiObj._eraserMaskGfx.destroyed) pixiObj._eraserMaskGfx.destroy()
				pixiObj._eraserMaskGfx = null
			}
			break
		}
		case 'group': {
			// Children managed separately by recursive call
			break
		}
	}
}

// ⚠️ `AlphaMask.init` clears `includeInBuild`/`renderable` but its `reset` restores only `measurable`, so an unmasked stencil stays dropped from the render build (StencilMask.reset restores both). Every site clearing a `mask` must go through here.
export const clearMask = pixiObj => {
	const stencil = pixiObj.mask
	pixiObj.mask = null
	if (stencil && !stencil.destroyed) {
		stencil.includeInBuild = true
		stencil.renderable = true
		stencil.measurable = true
	}
}

// Tear down one mapped entry. Every removal path goes through here.
//
// ⚠️ Destroying an object still serving as another's stencil leaves a dangling `pixiObj.mask`. PixiJS nulls the bind group behind the pooled `MaskFilter` but keeps the filter alive, so the next mask push throws `Cannot read properties of null (reading '0')` from inside the renderer with nothing in the frames naming us — clearing the link here is what keeps that unreachable.
//
// Guards mirror `_settleMaskCrossings` in transitionManager, which shares the `mask` property: an image eraser keeps its own Graphics on that same property (nulling it un-erases the image), and a live mask crossing installs its temp container there for the timeline to animate.
//
// `dependents` is the stencil → masked-objects index. Pass one when destroying a run of objects — building it per call is quadratic over a pass that is already O(tree) per keystroke.
export const buildMaskDependents = pixiMap => {
	const dependents = new Map()
	for (const { pixiObj: shape, fill } of pixiMap.values())
		for (const pixiObj of [shape, fill]) {
			const stencil = pixiObj?.mask
			if (!stencil || pixiObj.destroyed || pixiObj._eraserMaskGfx) continue
			const list = dependents.get(stencil)
			if (list) list.push(pixiObj)
			else dependents.set(stencil, [pixiObj])
		}
	return dependents
}

export const destroyEntry = (id, pixiMap, dependents) => {
	const entry = pixiMap.get(id)
	if (!entry) return
	const { pixiObj } = entry
	destroyFill(id, entry)

	// ⚠️ The clear must NOT be gated on `pixi_transition_active` while the destroy below is unconditional — that is half an operation, and it leaves every dependent pointing at a destroyed stencil (Rollbar web #410).
	//
	// The gate's intent survives as a RECHECK, with no heuristic: a mask the timeline is animating is a TEMP CONTAINER the transition installed on `other.mask`, never the raw stencil being destroyed, so it fails this test and keeps its coverage. It also defends against a stale passed-in `dependents` map. A one-frame coverage loss on a genuinely-destroyed stencil beats the renderer crash.
	for (const other of (dependents ?? buildMaskDependents(pixiMap)).get(pixiObj) ?? []) {
		if (other === pixiObj || other.destroyed || other._eraserMaskGfx) continue
		if (other.mask !== pixiObj) continue
		clearMask(other)
		other._maskInverse = undefined
		if (other._maskOptions) other._maskOptions.inverse = false
	}

	if (pixiObj._eraserMaskGfx) {
		pixiObj._eraserMaskGfx.parent?.removeChild(pixiObj._eraserMaskGfx)
		if (!pixiObj._eraserMaskGfx.destroyed) pixiObj._eraserMaskGfx.destroy()
		pixiObj._eraserMaskGfx = null
		pixiObj.mask = null
	}

	pixiObj.parent?.removeChild(pixiObj)
	if (!pixiObj.destroyed) pixiObj.destroy()
}

const loadImageTexture = (pixiObj, obj, assets) => {
	if (!obj.source) return
	const displayUrl = resolveAssetUrl(obj.source, assets)
	if (!displayUrl) return
	if (pixiObj._lastSource === displayUrl) return
	pixiObj._lastSource = displayUrl
	const init = key => obj[key] ?? getObjectInitial('image', key)
	const width = init('width')
	const height = init('height')

	const apply = tex => {
		pixiObj.texture = tex
		// A fade owns the pose of everything it carries, and composes its own scale against the group — `_loadFadeInImageTexture` does this for the sprites it created.
		if (window.pixi_transition_active) return
		// ⚠️ Never `pixiObj.width`/`height` here: those setters preserve the sign of `scale`, which on a sprite created against an EMPTY texture is Pixi's +1, so a mirrored image would render unmirrored for the sprite's whole life. Compose the signed product, as the `image` branch of `updatePixiObject` does.
		const sx = init('scaleX')
		const sy = init('scaleY')
		pixiObj.scale.x = (sx * width) / (tex.width || 1)
		pixiObj.scale.y = (sy * height) / (tex.height || 1)
	}

	// ⚠️ Synchronous, or the frame `updatePixiObject` already scaled for this texture draws the outgoing one at the incoming size.
	const cached = getCachedTexture(obj.source)
	if (cached) {
		apply(cached)
		return
	}

	const load = loadImageTextureFor(obj.source)
	if (!load) return
	load.then(apply).catch(e => logger.warn('Image load error', obj.source, e))
}

export const syncPixiScene = (
	objects,
	{ app, assets, selectedCamera, sourceSettings, pixiMap, bgRef, mounted = true }
) => {
	const stage = app.stage
	// What a mapped node resolves its references through: the objects this pass syncs are the live ones (Redux holds the FROM preset mid-fade).
	const env = { objects }

	// Render root background fill
	if (!bgRef.current) {
		bgRef.current = new Graphics()
		// Its draws span the canvas, 0..width.
		bgRef.current.position.set(STAGE_LEFT, STAGE_TOP)
		stage.addChildAt(bgRef.current, 0)
	}
	const bg = bgRef.current
	if (stage.children[0] !== bg) stage.setChildIndex(bg, 0)
	const rootFill = objects.root?.fill
	if (rootFill) {
		if (!window.pixi_transition_active) drawBgFill(bg, rootFill, app.screen.width, app.screen.height)
	} else if (!window.pixi_transition_active) {
		bg.clear()
	}

	// Collect all valid object ids by walking the tree from root
	const activeIds = new Set()
	const collectIds = parentId => {
		const parent = objects[parentId]
		if (!parent?.children) return
		for (const childId of parent.children) {
			if (!objects[childId]) continue
			activeIds.add(childId)
			if (objects[childId].type === 'group') collectIds(childId)
		}
	}
	collectIds('root')

	// Remove objects no longer in the tree. Key snapshot + shared index: destroyEntry reads pixiMap, so iterating it live would be concurrent modification.
	const stale = [...pixiMap.keys()].filter(id => !activeIds.has(id))
	if (stale.length) {
		const dependents = buildMaskDependents(pixiMap)
		for (const id of stale) {
			destroyEntry(id, pixiMap, dependents)
			pixiMap.delete(id)
		}
	}

	// Recursively sync children into their parent container
	const syncChildren = (parentContainer, parentId) => {
		const parent = objects[parentId]
		if (!parent?.children) return
		// The Pixi child index: a fill node takes the slot below its shape.
		let slot = parentContainer === stage && bgRef.current ? 1 : 0
		const place = pixiObj => {
			const targetIndex = slot++
			if (pixiObj.parent !== parentContainer || targetIndex >= parentContainer.children.length) return
			if (parentContainer.getChildAt(targetIndex) !== pixiObj)
				parentContainer.setChildIndex(pixiObj, Math.min(targetIndex, parentContainer.children.length - 1))
		}

		parent.children.forEach(childId => {
			const obj = objects[childId]
			if (!obj) return

			let entry = pixiMap.get(childId)

			if (!entry || entry.type !== obj.type) {
				// The delete must survive a null create below — a stranded destroyed entry re-matches later and the renderer throws.
				if (entry) {
					destroyEntry(childId, pixiMap)
					pixiMap.delete(childId)
				}
				const pixiObj = createPixiObject(obj)
				if (!pixiObj) return
				entry = { pixiObj, type: obj.type }
				pixiMap.set(childId, entry)
				parentContainer.addChild(pixiObj)
			}

			// Ensure parented to correct container. Skipped during a transition: Redux `objects`
			// stays at the FROM preset for the whole morph, so re-running this mid-morph would
			// yank a group⇄ungroup crosser back into its FROM container and undo the reparent that
			// transitionManager performed to hold it at its true world position. The transition
			// owns object parenting/transforms until it completes (like updatePixiObject below).
			//
			// ⚠️ An ORPHAN is never held: a group React destroyed detaches its children, and nothing else will put one back.
			const fadeOwns = !!window.pixi_transition_active && !!entry.pixiObj.parent

			if (!fadeOwns && entry.pixiObj.parent !== parentContainer) {
				parentContainer.addChild(entry.pixiObj)
			}

			if (!fadeOwns) {
				updatePixiObject(entry.pixiObj, obj)
				syncFill(childId, entry, obj, { env, selectedCamera, sourceSettings })
			}

			// ⚠️ `!fadeOwns` too: a fade that swapped the sprite to its DESTINATION texture must not have Redux — still naming the origin source — swap it straight back.
			if (obj.type === 'image' && !fadeOwns) loadImageTexture(entry.pixiObj, obj, assets)

			// Maintain z-order within this container. Requires actual membership: during a
			// transition the reparent above is skipped, so pixiObj may still live in the
			// container transitionManager moved it to — setChildIndex on a non-child throws
			// ("The supplied Container must be a child of the caller"). The transition owns
			// z-order too until it completes.
			if (fadeOwns) slot += entry.fill ? 2 : 1
			else {
				if (entry.fill) place(entry.fill)
				place(entry.pixiObj)
			}

			// Recurse into groups
			if (obj.type === 'group') {
				syncChildren(entry.pixiObj, childId)
			}
		})
	}

	syncChildren(stage, 'root')

	// Apply / clear PixiJS masks for all active objects.
	//
	// Gated on the transition flag like the alpha/visible sync above: Redux stays at the FROM preset for the whole morph, so re-running this mid-fade would tear out transitionManager's temporary coverage container and re-assert the FROM link — the mask would snap back on every unrelated Redux change during a fade. The transition owns masks until it completes and settles them itself (see §Mask crossings in context/transitions.md).
	if (!window.pixi_transition_active)
		for (const id of activeIds) {
			const obj = objects[id]
			const entry = pixiMap.get(id)
			if (!entry || !obj) continue
			if (obj.mask) {
				const maskEntry = pixiMap.get(obj.mask)
				const maskPixiObj = maskEntry?.pixiObj ?? null
				if (maskPixiObj) {
					const curMask = entry.pixiObj.mask
					const inverse = !!obj.maskInverse
					if (curMask !== maskPixiObj || entry.pixiObj._maskInverse !== inverse) {
						maskPixiObj._useAlphaMask = true
						// Ensure mask children without fill render as white (red channel = 1 for alpha mask)
						const maskObj = objects[obj.mask]
						if (maskObj?.children) {
							for (const childId of maskObj.children) {
								const childObj = objects[childId]
								const childEntry = pixiMap.get(childId)
								if (
									childEntry &&
									childObj &&
									childObj.type !== 'group' &&
									!childObj.fill &&
									childObj.blendMode !== 'destination-out'
								) {
									childEntry.pixiObj.updateStyle(
										{ color: 0xffffff, alpha: 1 },
										childObj.stroke,
										childObj.strokeWidth
									)
									childEntry.pixiObj._draw()
								}
							}
						}
						entry.pixiObj.setMask({ mask: maskPixiObj, inverse })
						entry.pixiObj._maskInverse = inverse
					}
					const fill = entry.fill
					if (fill && (fill.mask !== maskPixiObj || fill._maskInverse !== inverse)) {
						fill.setMask({ mask: maskPixiObj, inverse })
						fill._maskInverse = inverse
					}
				}
			} else {
				if (entry.pixiObj.mask && !entry.pixiObj._eraserMaskGfx) clearMask(entry.pixiObj)
				if (entry.fill?.mask) clearMask(entry.fill)
			}
			// A destination-only stencil may not have had a real Pixi object when
			// the transition settled. Keep its completed coverage mask until this
			// reconcile installs the destination stencil, then release it atomically.
			const heldMask = entry.pixiObj._transitionMaskContainer
			if (heldMask && entry.pixiObj.mask !== heldMask) {
				heldMask.parent?.removeChild(heldMask)
				if (!heldMask.destroyed) heldMask.destroy({ children: true })
				entry.pixiObj._transitionMaskContainer = null
			}
		}

	// Trigger a render (ticker is stopped, so we render manually). Skipped once unmounting: this can run against a scene the unmount cleanup is already tearing down.
	if (mounted && !window.pixi_render_clock_active) app.renderer.render(app.stage)
}

const usePixiObjects = app => {
	const objects = useSelector(selectObjects)
	const selectedCamera = useSelector(selectCameraDevice)
	const sourceSettings = useSelector(selectSourceSettings)
	const { data: assets } = useGetAssetsQuery()
	const pixiMapRef = useRef(new Map()) // id → { pixiObj, type }
	const bgRef = useRef(null) // Background Graphics for root fill
	const mountedRef = useRef(true)
	// The seam below outlives any one render; this ref is how it reaches the current props.
	const envRef = useRef({})
	envRef.current = { app, assets, selectedCamera, sourceSettings, objects }
	// What a fill node resolves its shape through; mid-fade the live objects are the FROM preset, so a node built for TO passes TO (record or Map).
	const meshEnv = objects => ({ objects: objects ?? envRef.current.objects })

	// Expose globals for direct manipulation during drags (bypass Redux)
	useEffect(() => {
		window.pixi_objects_map = pixiMapRef.current
		// A source's bag onto every fill node showing it, Redux untouched: an automation sample or a gesture frame, or Redux's own once it ends.
		window.pixi_write_source = (source, values) => {
			const { objects } = envRef.current
			let wrote = false
			for (const [id, { fill }] of pixiMapRef.current) {
				if (fill?._fillSource !== source || fill.destroyed || !objects?.[id]) continue
				// A path fade writes its node's vector itself; a write to its B moves the path's target.
				if (fill._path) {
					if (fill._path.source !== source || !fill._path.retarget) continue
					fill._path.retarget(editsOver(source, values))
					wrote = true
					continue
				}
				if (window.pixi_transition_active) {
					if (fill._sample) writeSampleValues(fill, values)
					else writeShaderValues(fill, values)
				} else {
					const nodeObj = fillNodeObj(objects[id], { [source]: values })
					const init = key => nodeObj[key]
					if (fill._sample) updateSampleMesh(fill, nodeObj, init, meshEnv())
					else updateShaderMesh(fill, nodeObj, init, meshEnv())
				}
				wrote = true
			}
			if (wrote && !window.pixi_render_clock_active) envRef.current.app?.renderer.render(envRef.current.app.stage)
		}
		window.pixi_update_object = updatePixiObject
		window.pixi_create_object = createPixiObject
		// A fade's fill seams: `entry` is a map entry or one the fade owns; `objects` and `bags` are the side the node shows.
		const fillCtx = ({ objects, bags } = {}) => ({
			env: meshEnv(objects),
			selectedCamera: envRef.current.selectedCamera,
			sourceSettings: bags ?? envRef.current.sourceSettings,
		})
		window.pixi_sync_fill = (id, entry, obj, side) => syncFill(id, entry, obj, fillCtx(side))
		window.pixi_swap_fill = (id, entry, obj, side) => swapFill(id, entry, obj, fillCtx(side))
		window.pixi_destroy_fill = destroyFill
		window.pixi_drop_fill_node = dropFillNode
		window.pixi_bg_ref = bgRef
		// Mask-crossing factories for transitionManager (see §Mask crossings in context/transitions.md). Exposed here rather than imported there so the manager stays decoupled from PixiJS and the unit suite can drive it over fakes.
		window.pixi_create_container = () => new Container()
		// ⚠️ A PixiShape, not a bare Graphics: the coverage shape is MORPHED onto the stencil's geometry, and `_rawPath`/`_draw` are what MorphPlugin drives.
		window.pixi_create_cover_rect = (w, h) => {
			const g = new PixiShape()
			// White + fully opaque: an alpha mask samples the red channel, so this is what "cover everything" means.
			g.updateStyle({ color: 0xffffff, alpha: 1 }, null, 1)
			// One closed rectangle over the stage frame (origin at its centre) as a cubic chain: 2 + 4×6 values. Control points sit on the edges, so each side stays straight. prettier-ignore
			g.setRawPathSilent([
				[
					0,
					0,
					w / 3,
					0,
					(2 * w) / 3,
					0,
					w,
					0,
					w,
					h / 3,
					w,
					(2 * h) / 3,
					w,
					h,
					(2 * w) / 3,
					h,
					w / 3,
					h,
					0,
					h,
					0,
					(2 * h) / 3,
					0,
					h / 3,
					0,
					0,
				].map((v, i) => v + (i % 2 ? STAGE_TOP : STAGE_LEFT)),
			])
			g._draw()
			return g
		}
		// Wraps an already-rendered texture for the mirror crossfade's frozen copy. Identity transform — the caller places it at the rendered box.
		window.pixi_create_sprite = texture => new Sprite(texture)
		// One FULL reconcile to the passed objects map: lowers `pixi_transition_active` for the pass, so every gated step runs. transitionManager calls this at non-carry exits and at run hand-over boundaries.
		window.pixi_sync_scene = objects => {
			const env = envRef.current
			if (!env.app) return
			const wasActive = window.pixi_transition_active
			window.pixi_transition_active = false
			try {
				syncPixiScene(objects, { ...env, pixiMap: pixiMapRef.current, bgRef, mounted: mountedRef.current })
			} finally {
				window.pixi_transition_active = wasActive
			}
		}
		return () => {
			window.pixi_objects_map = null
			window.pixi_write_source = null
			window.pixi_update_object = null
			window.pixi_create_object = null
			window.pixi_sync_fill = null
			window.pixi_swap_fill = null
			window.pixi_destroy_fill = null
			window.pixi_drop_fill_node = null
			window.pixi_bg_ref = null
			window.pixi_create_container = null
			window.pixi_create_cover_rect = null
			window.pixi_create_sprite = null
			window.pixi_sync_scene = null
		}
	}, [])

	useEffect(() => {
		if (!app) return
		syncPixiScene(objects, {
			app,
			assets,
			selectedCamera,
			sourceSettings,
			pixiMap: pixiMapRef.current,
			bgRef,
			mounted: mountedRef.current,
		})
	}, [app, objects, selectedCamera, sourceSettings, assets])

	// A held switch completes when the wanted source's first frame lands, which no dep above sees.
	useEffect(
		() =>
			onFirstFrame(() => {
				const { app, objects, selectedCamera, sourceSettings } = envRef.current
				if (!app) return
				let filled = false
				for (const [id, entry] of pixiMapRef.current) {
					const { pixiObj } = entry
					if (pixiObj.destroyed || !objects?.[id]) continue
					// A fill's aspect arrives with its first frame too.
					if (entry.fill?._sample && !window.pixi_transition_active) {
						syncFill(id, entry, objects[id], { env: meshEnv(), selectedCamera, sourceSettings })
						filled = true
					}
				}
				if (filled && !window.pixi_render_clock_active) app.renderer.render(app.stage)
			}),
		[]
	)

	// Dev: an edited shader module rebuilds every fill showing it, clock carried; a build that fails keeps the old node.
	useEffect(
		() =>
			onShadersChanged(names => {
				forgetShaderBuilds(names)
				const { app, objects, selectedCamera, sourceSettings } = envRef.current
				if (!app || window.pixi_transition_active) return
				for (const [id, entry] of pixiMapRef.current) {
					const old = entry.fill
					if (!old || old.destroyed || !names.includes(old._shaderName) || !objects?.[id]) continue
					entry.fill = null
					entry.fillBacking = null
					syncFill(id, entry, objects[id], { env: meshEnv(), selectedCamera, sourceSettings })
					if (!entry.fill) {
						entry.fill = old
						entry.fillBacking = fillBacking(objects[id])
						continue
					}
					carryShaderClock(old, entry.fill)
					dropFillNode(old)
				}
				if (!window.pixi_render_clock_active) app.renderer.render(app.stage)
			}),
		[]
	)

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			mountedRef.current = false
			const pixiMap = pixiMapRef.current
			const dependents = buildMaskDependents(pixiMap)
			for (const id of [...pixiMap.keys()]) destroyEntry(id, pixiMap, dependents)
			pixiMap.clear()
		}
	}, [])
}

export default usePixiObjects
